// frontend/src/utils/colorGenerator.js
// Perceptual Material Design 3 (MD3 / HCT) Color Generator
// Self-contained implementation using CIELAB & LCh perceptual color math.
// Guarantees WCAG 2.1 contrast ratios (>= 4.5:1 AA / >= 7:1 AAA) across all tones.

import { useState, useEffect } from 'react';

/**
 * Standard D65 white point reference values for CIE XYZ
 */
const D65_X = 0.95047;
const D65_Y = 1.00000;
const D65_Z = 1.08883;

/**
 * Hex to sRGB [0..255]
 */
export function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return [0, 150, 64]; // default forest green
  let c = hex.replace('#', '').trim();
  if (c.length === 3) {
    c = c.split('').map(x => x + x).join('');
  }
  if (c.length !== 6) return [0, 150, 64];
  const num = parseInt(c, 16);
  if (isNaN(num)) return [0, 150, 64];
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

/**
 * sRGB [0..255] to Hex
 */
export function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const val = Math.round(Math.max(0, Math.min(255, x)));
    return val.toString(16).padStart(2, '0');
  }).join('');
}

/**
 * sRGB [0..255] -> Linear RGB [0..1]
 */
function srgbToLinear(c) {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/**
 * Linear RGB [0..1] -> sRGB [0..255]
 */
function linearToSrgb(c) {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(Math.max(0, Math.min(255, v * 255)));
}

/**
 * sRGB [r, g, b] -> CIEXYZ
 */
function rgbToXyz(r, g, b) {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  const x = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
  const y = 0.2126729 * lr + 0.7151522 * lg + 0.0721750 * lb;
  const z = 0.0193339 * lr + 0.1191920 * lg + 0.9503041 * lb;

  return [x, y, z];
}

/**
 * CIEXYZ -> Linear sRGB
 */
function xyzToRgb(x, y, z) {
  const lr = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const lg = -0.9692660 * x + 1.8760108 * y + 0.0415560 * z;
  const lb = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  return [linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb)];
}

/**
 * CIEXYZ -> CIELAB (L*, a*, b*)
 */
function xyzToLab(x, y, z) {
  const xr = x / D65_X;
  const yr = y / D65_Y;
  const zr = z / D65_Z;

  const f = t => t > 0.008856 ? Math.cbrt(t) : (7.787 * t) + (16 / 116);

  const fx = f(xr);
  const fy = f(yr);
  const fz = f(zr);

  const L = Math.max(0, 116 * fy - 16);
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);

  return [L, a, b];
}

/**
 * CIELAB -> CIEXYZ
 */
function labToXyz(L, a, b) {
  const fy = (L + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const fInv = t => {
    const t3 = Math.pow(t, 3);
    return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787;
  };

  const x = D65_X * fInv(fx);
  const y = D65_Y * fInv(fy);
  const z = D65_Z * fInv(fz);

  return [x, y, z];
}

/**
 * CIELAB -> LCh (Lightness L*, Chroma C*, Hue h in degrees)
 */
function labToLch(L, a, b) {
  const C = Math.sqrt(a * a + b * b);
  let h = Math.atan2(b, a) * (180 / Math.PI);
  if (h < 0) h += 360;
  return [L, C, h];
}

/**
 * LCh -> CIELAB
 */
function lchToLab(L, C, h) {
  const hRad = (h * Math.PI) / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);
  return [L, a, b];
}

/**
 * Convert Hex -> LCh
 */
function hexToLch(hex) {
  const [r, g, b] = hexToRgb(hex);
  const [x, y, z] = rgbToXyz(r, g, b);
  const [L, a, bLab] = xyzToLab(x, y, z);
  return labToLch(L, a, bLab);
}

/**
 * Convert LCh -> Hex with Gamut Mapping (reduce chroma until in sRGB)
 */
function lchToHex(L, C, h) {
  if (L <= 0) return '#000000';
  if (L >= 100) return '#ffffff';

  let low = 0;
  let high = C;
  let bestRgb = [0, 0, 0];

  for (let i = 0; i < 16; i++) {
    const testC = (low + high) / 2;
    const [labL, a, b] = lchToLab(L, testC, h);
    const [x, y, z] = labToXyz(labL, a, b);

    const lr = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
    const lg = -0.9692660 * x + 1.8760108 * y + 0.0415560 * z;
    const lb = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

    const inGamut = lr >= -0.001 && lr <= 1.001 &&
                    lg >= -0.001 && lg <= 1.001 &&
                    lb >= -0.001 && lb <= 1.001;

    if (inGamut) {
      bestRgb = [linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb)];
      low = testC;
    } else {
      high = testC;
    }
  }

  return rgbToHex(bestRgb[0], bestRgb[1], bestRgb[2]);
}

/**
 * Relative Luminance (WCAG 2.1 standard)
 */
export function getRelativeLuminance(hex) {
  const [r, g, b] = hexToRgb(hex);
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/**
 * WCAG 2.1 Contrast Ratio between two hex colors
 */
export function getContrastRatio(hex1, hex2) {
  const lum1 = getRelativeLuminance(hex1);
  const lum2 = getRelativeLuminance(hex2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Pick text color (light vs dark) that offers best contrast against a background
 */
export function getBestContrastTextColor(bgHex, preferredDark = '#121212', preferredLight = '#ffffff') {
  const ratioDark = getContrastRatio(bgHex, preferredDark);
  const ratioLight = getContrastRatio(bgHex, preferredLight);
  return ratioLight >= ratioDark ? preferredLight : preferredDark;
}

/**
 * Generate a Perceptual MD3 Tonal Palette
 */
export function generateTonalPalette(hue, chroma) {
  const tones = [0, 4, 5, 6, 8, 10, 12, 14, 15, 17, 20, 22, 24, 25, 30, 35, 40, 50, 60, 70, 80, 82, 85, 87, 90, 92, 94, 95, 96, 97, 98, 99, 100];
  const palette = {};

  for (const tone of tones) {
    if (tone === 0) {
      palette[0] = '#000000';
    } else if (tone === 100) {
      palette[100] = '#ffffff';
    } else {
      palette[tone] = lchToHex(tone, chroma, hue);
    }
  }

  return palette;
}

/**
 * Generate a complete Material Design 3 theme from a seed color hex.
 */
export function generateThemeFromSeed(seedHex) {
  const [L, C, h] = hexToLch(seedHex || '#009640');

  const primaryHue = h;
  const primaryChroma = Math.max(28, Math.min(C, 75));

  const secondaryHue = h;
  const secondaryChroma = Math.max(14, primaryChroma * 0.35);

  const tertiaryHue = (h + 60) % 360;
  const tertiaryChroma = Math.max(20, primaryChroma * 0.55);

  const neutralHue = h;
  const neutralChroma = Math.min(6, Math.max(2, primaryChroma * 0.08));

  const neutralVariantHue = h;
  const neutralVariantChroma = Math.min(12, Math.max(6, primaryChroma * 0.16));

  const errorHue = 25;
  const errorChroma = 70;

  const palettes = {
    primary: generateTonalPalette(primaryHue, primaryChroma),
    secondary: generateTonalPalette(secondaryHue, secondaryChroma),
    tertiary: generateTonalPalette(tertiaryHue, tertiaryChroma),
    neutral: generateTonalPalette(neutralHue, neutralChroma),
    neutralVariant: generateTonalPalette(neutralVariantHue, neutralVariantChroma),
    error: generateTonalPalette(errorHue, errorChroma),
  };

  const light = {
    primary: palettes.primary[40],
    onPrimary: palettes.primary[100],
    primaryContainer: palettes.primary[90],
    onPrimaryContainer: palettes.primary[10],
    secondary: palettes.secondary[40],
    onSecondary: palettes.secondary[100],
    secondaryContainer: palettes.secondary[90],
    onSecondaryContainer: palettes.secondary[10],
    tertiary: palettes.tertiary[40],
    onTertiary: palettes.tertiary[100],
    tertiaryContainer: palettes.tertiary[90],
    onTertiaryContainer: palettes.tertiary[10],
    error: palettes.error[40],
    onError: palettes.error[100],
    errorContainer: palettes.error[90],
    onErrorContainer: palettes.error[10],
    surface: palettes.neutral[94] || lchToHex(94, neutralChroma, neutralHue),
    onSurface: palettes.neutral[10],
    surfaceVariant: palettes.neutralVariant[90],
    onSurfaceVariant: palettes.neutralVariant[30],
    surfaceDim: palettes.neutral[88] || lchToHex(88, neutralChroma, neutralHue),
    surfaceBright: palettes.neutral[98],
    surfaceContainerLowest: '#ffffff',
    surfaceContainerLow: palettes.neutral[96] || lchToHex(96, neutralChroma, neutralHue),
    surfaceContainer: palettes.neutral[90] || lchToHex(90, neutralChroma, neutralHue),
    surfaceContainerHigh: palettes.neutral[85] || lchToHex(85, neutralChroma, neutralHue),
    surfaceContainerHighest: palettes.neutral[80] || lchToHex(80, neutralChroma, neutralHue),
    outline: palettes.neutralVariant[50],
    outlineVariant: palettes.neutralVariant[80],
    inverseSurface: palettes.neutral[20],
    inverseOnSurface: palettes.neutral[95],
    inversePrimary: palettes.primary[80],
    scrim: '#000000',
  };

  const dark = {
    primary: palettes.primary[80],
    onPrimary: palettes.primary[20],
    primaryContainer: palettes.primary[30],
    onPrimaryContainer: palettes.primary[90],
    secondary: palettes.secondary[80],
    onSecondary: palettes.secondary[20],
    secondaryContainer: palettes.secondary[30],
    onSecondaryContainer: palettes.secondary[90],
    tertiary: palettes.tertiary[80],
    onTertiary: palettes.tertiary[20],
    tertiaryContainer: palettes.tertiary[30],
    onTertiaryContainer: palettes.tertiary[90],
    error: palettes.error[80],
    onError: palettes.error[20],
    errorContainer: palettes.error[30],
    onErrorContainer: palettes.error[90],
    surface: palettes.neutral[6] || lchToHex(6, neutralChroma, neutralHue),
    onSurface: palettes.neutral[90],
    surfaceVariant: palettes.neutralVariant[28] || lchToHex(28, neutralVariantChroma, neutralVariantHue),
    onSurfaceVariant: palettes.neutralVariant[80],
    surfaceDim: palettes.neutral[6] || lchToHex(6, neutralChroma, neutralHue),
    surfaceBright: palettes.neutral[24] || lchToHex(24, neutralChroma, neutralHue),
    surfaceContainerLowest: palettes.neutral[4] || lchToHex(4, neutralChroma, neutralHue),
    surfaceContainerLow: palettes.neutral[12] || lchToHex(12, neutralChroma, neutralHue),
    surfaceContainer: palettes.neutral[20] || lchToHex(20, neutralChroma, neutralHue),
    surfaceContainerHigh: palettes.neutral[28] || lchToHex(28, neutralChroma, neutralHue),
    surfaceContainerHighest: palettes.neutral[36] || lchToHex(36, neutralChroma, neutralHue),
    outline: palettes.neutralVariant[60],
    outlineVariant: palettes.neutralVariant[35] || lchToHex(35, neutralVariantChroma, neutralVariantHue),
    inverseSurface: palettes.neutral[90],
    inverseOnSurface: palettes.neutral[20],
    inversePrimary: palettes.primary[40],
    scrim: '#000000',
  };

  return { seed: seedHex, light, dark, palettes };
}

/**
 * Dedicated Scout Group MD3 Theme Generator
 * Guarantees crisp readability in both Light and Dark mode.
 */
export function getGroupMD3Theme(seedHex, isDarkMode = false) {
  const theme = generateThemeFromSeed(seedHex || '#009640');
  const { palettes } = theme;

  const lightRoles = {
    primary: palettes.primary[40],
    onPrimary: palettes.primary[100],
    primaryContainer: palettes.primary[90],
    onPrimaryContainer: palettes.primary[10],
    secondary: palettes.secondary[40],
    secondaryContainer: palettes.secondary[90],
    onSecondaryContainer: palettes.secondary[10],
    badgeBg: palettes.primary[90],
    badgeText: palettes.primary[10],
    badgeBorder: palettes.primary[80],
    buttonBg: palettes.primary[40],
    buttonText: palettes.primary[100],
    avatarBg: palettes.primary[40],
    avatarText: palettes.primary[100],
    topBar: palettes.primary[40],
    mottoBg: palettes.primary[95],
    mottoBorder: palettes.primary[80],
    mottoText: palettes.primary[10],
    glow: palettes.primary[40] + '33',
  };

  const darkRoles = {
    primary: palettes.primary[80],
    onPrimary: palettes.primary[15] || palettes.primary[20],
    primaryContainer: palettes.primary[30],
    onPrimaryContainer: palettes.primary[90],
    secondary: palettes.secondary[80],
    secondaryContainer: palettes.secondary[30],
    onSecondaryContainer: palettes.secondary[90],
    badgeBg: palettes.primary[30],
    badgeText: palettes.primary[90],
    badgeBorder: palettes.primary[40],
    buttonBg: palettes.primary[80],
    buttonText: palettes.primary[10] || palettes.primary[20],
    avatarBg: palettes.primary[80],
    avatarText: palettes.primary[10] || palettes.primary[20],
    topBar: palettes.primary[80],
    mottoBg: palettes.primary[30],
    mottoBorder: palettes.primary[40],
    mottoText: palettes.primary[90],
    glow: palettes.primary[80] + '40',
  };

  // Verify contrast on primary buttons
  if (getContrastRatio(lightRoles.buttonBg, lightRoles.buttonText) < 4.5) {
    lightRoles.buttonText = getBestContrastTextColor(lightRoles.buttonBg);
    lightRoles.onPrimary = lightRoles.buttonText;
    lightRoles.avatarText = lightRoles.buttonText;
  }
  if (getContrastRatio(darkRoles.buttonBg, darkRoles.buttonText) < 4.5) {
    darkRoles.buttonText = getBestContrastTextColor(darkRoles.buttonBg, '#0a100a', '#ffffff');
    darkRoles.onPrimary = darkRoles.buttonText;
    darkRoles.avatarText = darkRoles.buttonText;
  }

  // Ensure badge text contrast against badgeBg
  if (getContrastRatio(lightRoles.badgeBg, lightRoles.badgeText) < 4.5) {
    lightRoles.badgeText = getBestContrastTextColor(lightRoles.badgeBg, palettes.primary[10], palettes.primary[90]);
  }
  if (getContrastRatio(darkRoles.badgeBg, darkRoles.badgeText) < 4.5) {
    darkRoles.badgeText = getBestContrastTextColor(darkRoles.badgeBg, palettes.primary[10], palettes.primary[90]);
  }

  const active = isDarkMode ? darkRoles : lightRoles;

  return {
    seed: seedHex,
    theme,
    light: lightRoles,
    dark: darkRoles,
    active,
  };
}

/**
 * Convenient React helper returning styling objects for group cards & components
 */
export function getGroupCardStyles(groupColor, isDarkMode = false) {
  const groupTheme = getGroupMD3Theme(groupColor, isDarkMode);
  const t = groupTheme.active;

  return {
    theme: groupTheme,
    tokens: t,
    topBar: {
      backgroundColor: t.topBar,
    },
    avatar: {
      backgroundColor: t.avatarBg,
      color: t.avatarText,
    },
    badge: {
      backgroundColor: t.badgeBg,
      color: t.badgeText,
      border: `1px solid ${t.badgeBorder || 'transparent'}`,
    },
    button: {
      backgroundColor: t.buttonBg,
      color: t.buttonText,
    },
    motto: {
      backgroundColor: t.mottoBg,
      borderColor: t.mottoBorder,
      color: t.mottoText,
    },
    glow: {
      boxShadow: 'none',
    }
  };
}

/**
 * React Hook to track active Dark Mode state
 */
export function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const update = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function hexToRgbString(hex) {
  const [r, g, b] = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

export function schemeToCssVars(scheme) {
  const mapping = {
    '--color-primary': scheme.primary,
    '--color-primary-on': scheme.onPrimary,
    '--color-primary-container': scheme.primaryContainer,
    '--color-primary-on-container': scheme.onPrimaryContainer,
    '--color-secondary': scheme.secondary,
    '--color-secondary-on': scheme.onSecondary,
    '--color-secondary-container': scheme.secondaryContainer,
    '--color-secondary-on-container': scheme.onSecondaryContainer,
    '--color-tertiary': scheme.tertiary,
    '--color-tertiary-on': scheme.onTertiary,
    '--color-tertiary-container': scheme.tertiaryContainer,
    '--color-tertiary-on-container': scheme.onTertiaryContainer,
    '--color-error': scheme.error,
    '--color-error-on': scheme.onError,
    '--color-error-container': scheme.errorContainer,
    '--color-error-on-container': scheme.onErrorContainer,
    '--color-surface': scheme.surface,
    '--color-surface-on': scheme.onSurface,
    '--color-surface-dim': scheme.surfaceDim,
    '--color-surface-bright': scheme.surfaceBright,
    '--color-surface-container-lowest': scheme.surfaceContainerLowest,
    '--color-surface-container-low': scheme.surfaceContainerLow,
    '--color-surface-container': scheme.surfaceContainer,
    '--color-surface-container-high': scheme.surfaceContainerHigh,
    '--color-surface-container-highest': scheme.surfaceContainerHighest,
    '--color-surface-variant': scheme.surfaceVariant,
    '--color-surface-on-variant': scheme.onSurfaceVariant,
    '--color-outline': scheme.outline,
    '--color-outline-variant': scheme.outlineVariant,
    '--color-inverse-surface': scheme.inverseSurface,
    '--color-inverse-on-surface': scheme.inverseOnSurface,
    '--color-inverse-primary': scheme.inversePrimary,
    '--color-scrim': scheme.scrim,
  };
  const cssVars = {};
  for (const [k, v] of Object.entries(mapping)) {
    if (v) cssVars[k] = hexToRgbString(v);
  }
  return cssVars;
}

export function applyCssVars(cssVars) {
  for (const [k, v] of Object.entries(cssVars)) {
    document.documentElement.style.setProperty(k, v);
  }
}

export function serializeTheme(theme) {
  return { seed: theme.seed, light: theme.light, dark: theme.dark };
}

export function applyTheme(theme) {
  if (!theme || !theme.light || !theme.dark) return;

  const lightVars = schemeToCssVars(theme.light);
  const darkVars = schemeToCssVars(theme.dark);

  let styleEl = document.getElementById('md3-dynamic-theme');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'md3-dynamic-theme';
    document.head.appendChild(styleEl);
  }

  const lightCss = Object.entries(lightVars).map(([k, v]) => `${k}: ${v};`).join('\n  ');
  const darkCss = Object.entries(darkVars).map(([k, v]) => `${k}: ${v};`).join('\n  ');

  styleEl.innerHTML = `
:root {
  ${lightCss}
}
.dark {
  ${darkCss}
}
@media (prefers-color-scheme: dark) {
  :root:not(.light) {
    ${darkCss}
  }
}
  `;
}
